import { useEffect, useMemo, useState } from 'react'

export function useDelayedUpdate<T, K>(
    items: T[],
    delay: number,
    getIdentity: (item: T) => K,
): [T[], K[], K[]] {
    const [delayedItems, setDelayedItems] = useState<T[]>(items)
    const [isSyncing, setIsSyncing] = useState(false)

    useEffect(() => {
        if (Math.abs(items.length - delayedItems.length) !== 1) {
            setDelayedItems(items)

            return undefined
        }

        setIsSyncing(true)

        const handler = setTimeout(() => {
            setDelayedItems(items)
            setIsSyncing(false)
        }, delay)

        return () => clearTimeout(handler)
    }, [items, delay, delayedItems])

    const { addedIds, removedIds } = useMemo(() => {
        if (! isSyncing) return { addedIds: [], removedIds: [] }

        const targetIds = items.map(getIdentity)
        const currentIds = delayedItems.map(getIdentity)

        const targetSet = new Set(targetIds)
        const currentSet = new Set(currentIds)

        const added = targetIds.filter((id) => ! currentSet.has(id))
        const removed = currentIds.filter((id) => ! targetSet.has(id))

        return { addedIds: added, removedIds: removed }
    }, [items, delayedItems, isSyncing, getIdentity])

    return [delayedItems, addedIds, removedIds]
}
